import {
  CommandResponseMode,
  DeviceCommand,
} from "lib/deviceDrivers/sessionFramework/device/types";
import {
  createCommand,
  ScpCommandPayload,
} from "lib/deviceDrivers/truTest/scp/device/commands";

const COMMAND_TIMEOUT_MS = 3000;
const MAX_COMMAND_RETRIES = 2;

/**
 * Convenience wrapper around `createCommand` that fills in the common defaults for this driver.
 * Overrides can be passed in for commands that need non-standard timing or retry policy.
 */
function buildCommand(
  name: string,
  payload: string,
  responseMode: DeviceCommand["responseMode"],
  overrides: Partial<
    Pick<DeviceCommand, "timeoutMs" | "maxRetries" | "retryOnBusy">
  > = {},
): DeviceCommand {
  return createCommand(name, payload, responseMode, {
    timeoutMs: COMMAND_TIMEOUT_MS,
    maxRetries: MAX_COMMAND_RETRIES,
    retryOnBusy: true,
    ...overrides,
  });
}

/**
 * Requests a specific saved session from the scanner's session list.
 */
export function buildSelectSessionCommand(sessionId: string): DeviceCommand {
  return buildCommand(
    "selectSession",
    `{FF${sessionId}}`,
    CommandResponseMode.ACK,
  );
}

/**
 * Requests the user-visible name for one saved session.
 */
export function buildSessionNameCommand(sessionId: string): DeviceCommand {
  return buildCommand(
    "getSessionName",
    `{FPNA${sessionId}}`,
    CommandResponseMode.DATA,
  );
}

/**
 * Turns on ACKs and device error reporting before the driver asks for live scan format data.
 * Those settings make the scanner's later responses predictable enough for the protocol parser to follow.
 */
export function syncHardwareConfigCommands(): DeviceCommand[] {
  return [
    buildCommand(
      "enableAcks",
      ScpCommandPayload.ENABLE_ACKS,
      CommandResponseMode.ACK,
      {
        // HACK for iOS: Allow the system to retry a couple of times, until it is ready to message payloads.
        // The Scanner shows up as connected as soon as the BLE link is establised, which is before the GATT is initialised
        // You can only write to GATT attributes (in this case the Nordic UART TX attribute) after the GATT is ready.
        // The alternative to this is to rewrite the iOS app to only expose the device as connected once it has
        // completed its service discovery.
        maxRetries: 5,
        timeoutMs: 1000,
      },
    ),
    buildCommand(
      "enableErrors",
      ScpCommandPayload.ENABLE_ERRORS,
      CommandResponseMode.ACK,
      { maxRetries: 0 },
    ),
    buildCommand(
      "requestEidOutputFormat",
      ScpCommandPayload.QUERY_EID_OUTPUT_FORMAT,
      CommandResponseMode.DATA,
    ),
  ];
}

/**
 * Resets the record cursor before the saved-session import starts.
 */
export function resetSessionRecordCursorCommand(): DeviceCommand {
  return buildCommand(
    "resetSessionRecordCursor",
    ScpCommandPayload.RESET_SESSION_RECORD_CURSOR,
    CommandResponseMode.ACK,
  );
}

/**
 * Switches the scanner into the mode that exposes saved session data.
 */
export function selectSessionDataModeCommand(): DeviceCommand {
  return buildCommand(
    "selectSessionDataMode",
    ScpCommandPayload.SELECT_SESSION_DATA_MODE,
    CommandResponseMode.ACK,
  );
}

/**
 * Requests the number of saved sessions present on the scanner.
 */
export function getSessionCountCommand(): DeviceCommand {
  return buildCommand(
    "getSessionCount",
    ScpCommandPayload.GET_SESSION_COUNT,
    CommandResponseMode.DATA,
  );
}

/**
 * Requests the next saved session identifier from the scanner.
 */
export function getNextSessionIdCommand(): DeviceCommand {
  return buildCommand(
    "getNextSessionId",
    ScpCommandPayload.GET_NEXT_SESSION_ID,
    CommandResponseMode.DATA,
  );
}

/**
 * Requests the next field code while the scanner is still listing record metadata.
 */
export function getNextFieldCodeCommand(): DeviceCommand {
  return buildCommand(
    "getNextFieldCode",
    ScpCommandPayload.GET_NEXT_FIELD_CODE,
    CommandResponseMode.DATA,
  );
}

/**
 * Requests the next record in the current saved session.
 */
export function getNextRecordCommand(): DeviceCommand {
  return buildCommand(
    "getNextRecord",
    ScpCommandPayload.GET_NEXT_RECORD,
    CommandResponseMode.DATA,
  );
}
